import { WplywContext } from "../../../../../../../context/wplywContext";
import { useContext } from "react";
import LineSpacer from "../../../../../../../components/LineSpacer";
import { Button, Input, Select } from "antd";
import CalculatorInput from "../../../../../../../components/CalculatorInput";
import { useState } from "react";
import BankAccountInput from "../../../../../../../components/BankAccountInput";
import { useAuth } from "../../../../../../../context/AuthContext";
import FixedNumberInput from "../../../../../../../components/FixedNumberInput";
import Label from "../../../../../../../components/Label";
import {
	buildTransferTitlePreview,
	sanitizeSingleLineTitle,
} from "../../../../../../../components/transferTitleUtils";

const Transfer = ({ selectedPerson, transfer, index, total }) => {
	const { wplyw, setWplyw } = useContext(WplywContext);
	const [open, setOpen] = useState(true);
	const { user } = useAuth();
	const isGeneratedTransferLocked =
		Boolean(selectedPerson?.msr_persons_transfers_generated_file) ||
		Boolean(selectedPerson?.msr_reserves_generated_file) ||
		Boolean(
			(selectedPerson?.msr_transfers || []).some(
				(t) => t?.msr_transfers_generated_file,
			),
		);
	const isDisabled =
		wplyw.mode === "preview" ||
		isGeneratedTransferLocked ||
		transfer?.msr_transfers_confirmed;
	const transfertitle = buildTransferTitlePreview({
		personName: selectedPerson.msr_persons_name,
		personLastName: selectedPerson.msr_persons_lastname,
		wplywOwnId: wplyw.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
		personOwnId: selectedPerson.msr_persons_own_id,
		transferOwnId: transfer.msr_transfers_own_id,
		titleSuffix: transfer.msr_transfers_title_suffix,
	});

	const labelStyle = {
		color: "black",
		fontWeight: "500",
		fontSize: "16px",
		marginBottom: "8px",
	};
	const valueStyle = {
		color: "black",
		fontWeight: "700",
		fontSize: "16px",
	};

	const checkValues = (field, value) => {
		switch (field) {
			case "msr_transfers_types_id":
				return wplyw.wplyw_data.data.default_values.msr_transfers_types.find(
					(x) => x.value === value,
				).label;
			default:
				return value;
		}
	};

	const handleUpdateTransfer = (key, value) => {
		const fieldLabels = {
			msr_transfers_amount: "Kwota przelewu",
			msr_transfers_account_number: "Numer konta bankowego",
			msr_transfers_account_owner: "Właściciel konta",
			msr_transfers_title_suffix: "Sufiks tytułu przelewu",
			msr_transfers_types_id: "Typ przelewu",
		};

		const sanitizedValue =
			key === "msr_transfers_title_suffix"
				? sanitizeSingleLineTitle(value)
				: value;
		const updatedTransfer = { ...transfer, [key]: sanitizedValue };
		const updatedTransfertitle = buildTransferTitlePreview({
			personName: selectedPerson.msr_persons_name,
			personLastName: selectedPerson.msr_persons_lastname,
			wplywOwnId: wplyw.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
			personOwnId: selectedPerson.msr_persons_own_id,
			transferOwnId: transfer.msr_transfers_own_id,
			titleSuffix: updatedTransfer.msr_transfers_title_suffix,
		});

		if (
			updatedTransfertitle.length > 140 &&
			key === "msr_transfers_title_suffix" &&
			sanitizedValue.length >
				sanitizeSingleLineTitle(transfer.msr_transfers_title_suffix)
					.length
		) {
			return;
		}

		setWplyw((prev) => {
			const updatedTransfers = selectedPerson.msr_transfers.map((t) =>
				t.msr_transfers_id === transfer.msr_transfers_id
					? { ...t, [key]: sanitizedValue }
					: t,
			);

			const changes = [...(prev.changes || [])];
			const existingChangeIndex = changes.findIndex(
				(change) =>
					change.field === key &&
					change.objectid === transfer.msr_transfers_own_id,
			);

			const originalValue = transfer[key];

			if (existingChangeIndex > -1) {
				changes[existingChangeIndex] = {
					...changes[existingChangeIndex],
					newValue: checkValues(key, sanitizedValue),
					datetime: Math.floor(new Date().getTime() / 1000),
				};
			} else {
				changes.push({
					etap: "Przelew",
					field: key,
					label: fieldLabels[key] || key,
					action: "edit",
					oldValue: originalValue,
					newValue: checkValues(key, sanitizedValue),
					datetime: Math.floor(new Date().getTime() / 1000),
					user: user.user.mlg_users_id,
					objectid: transfer.msr_transfers_own_id,
					wplywid: wplyw.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
					personid: selectedPerson.msr_persons_own_id,
				});
			}

			return {
				...prev,
				wplyw_data: {
					...prev.wplyw_data,
					data: {
						...prev.wplyw_data.data,
						msr_wplywy: {
							...prev.wplyw_data.data.msr_wplywy,
							msr_persons:
								prev.wplyw_data.data.msr_wplywy.msr_persons.map(
									(person) =>
										person.msr_persons_id ===
										selectedPerson.msr_persons_id
											? {
													...person,
													msr_transfers:
														updatedTransfers,
												}
											: person,
								),
						},
					},
				},
				changes,
			};
		});
	};

	const handleRemoveTransfer = () => {
		setWplyw((prev) => {
			const updatedTransfers = selectedPerson.msr_transfers.filter(
				(t) => t.msr_transfers_id !== transfer.msr_transfers_id,
			);

			const changes = [...(prev.changes || [])];

			changes.push({
				etap: "Przelew",
				field: "msr_transfers",
				label: "Usunięcie przelewu",
				action: "remove",
				oldValue: `Usunięto przelew o ID ${transfer.msr_transfers_own_id}`,
				newValue: `Usunięto przelew o ID ${transfer.msr_transfers_own_id}`,
				datetime: Math.floor(new Date().getTime() / 1000),
				user: user.user.mlg_users_id,
				objectid: transfer.msr_transfers_own_id,
				wplywid: wplyw.wplyw_data.data.msr_wplywy.msr_wplywy_own_id,
				personid: selectedPerson.msr_persons_own_id,
			});

			return {
				...prev,
				wplyw_data: {
					...prev.wplyw_data,
					data: {
						...prev.wplyw_data.data,
						msr_wplywy: {
							...prev.wplyw_data.data.msr_wplywy,
							msr_persons:
								prev.wplyw_data.data.msr_wplywy.msr_persons.map(
									(person) =>
										person.msr_persons_id ===
										selectedPerson.msr_persons_id
											? {
													...person,
													msr_transfers:
														updatedTransfers,
												}
											: person,
								),
						},
					},
				},
				changes,
			};
		});
	};

	const isBankTransfer =
		wplyw?.wplyw_data?.data?.default_values?.msr_transfers_types.find(
			(x) => x.msr_transfers_types_id === transfer.msr_transfers_types_id,
		)?.msr_transfers_types_bank_transfer === 1;

	return (
		<div
			style={{
				background: "white",
				padding: "0",
				borderRadius: "5px",
				border: `1px solid ${
					transfer.msr_transfers_generated_file
						? "rgb(82, 196, 26)"
						: "rgb(189, 129, 54)"
				}`,
				marginBottom: "20px",
			}}
		>
			<div
				style={{
					padding: "20px",
					opacity:
						transfer.msr_transfers_confirmed ||
						transfer.msr_transfers_generated_file
							? "0.4"
							: "1",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "20px",
						alignItems: "center",
					}}
				>
					<div
						style={{
							background: "black",
							color: "white",
							padding: "7px 15px",
							fontSize: "16px",
							width: "fit-content",
							borderRadius: "5px",
						}}
					>
						#{index + 1} Wypłata #{transfer?.msr_transfers_own_id}
					</div>
					<LineSpacer />
					<Button onClick={() => setOpen(!open)}>
						{open ? "Zwiń" : "Rozwiń"}
					</Button>
					{!isDisabled && (
						<Button
							onClick={handleRemoveTransfer}
							danger
						>
							Usuń wypłatę
						</Button>
					)}
				</div>
				{open && (
					<>
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 1fr 150px",
								alignItems: "end",
								gap: "32px",
								marginTop: "16px",
							}}
						>
							<div>
								<Label required>
									{
										wplyw?.wplyw_data?.data?.default_values?.msr_transfers_types.find(
											(x) =>
												x.msr_transfers_types_id ===
												transfer.msr_transfers_types_id,
										)?.msr_transfers_types_amount_title
									}
								</Label>
								<FixedNumberInput
									mode={wplyw.mode}
									disabled={isDisabled}
									calculator={true}
									maxvalue={parseFloat(
										total -
											selectedPerson.msr_reserves_amount -
											selectedPerson.msr_transfers.reduce(
												(sum, tr) =>
													sum +
													(tr.msr_transfers_own_id !=
													transfer.msr_transfers_own_id
														? parseFloat(
																tr.msr_transfers_amount,
															)
														: 0),
												0,
											),
									).toFixed(2)}
									value={transfer.msr_transfers_amount}
									onChange={(e) =>
										handleUpdateTransfer(
											"msr_transfers_amount",
											e,
										)
									}
									addonAfter="zł"
								/>
							</div>
							<div>
								<Label required>Przedmiot kwoty wpływu</Label>
								<Select
									style={{ width: "100%" }}
									disabled={isDisabled}
									value={
										transfer.msr_transfers_types_id || ""
									}
									options={
										wplyw?.wplyw_data?.data?.default_values
											?.msr_transfers_types || []
									}
									onChange={(value) =>
										handleUpdateTransfer(
											"msr_transfers_types_id",
											value,
										)
									}
								/>
							</div>
						</div>
						{isBankTransfer && (
							<>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr 1fr",
										alignItems: "end",
										gap: "32px",
										marginTop: "16px",
									}}
								>
									<div>
										<Label required>
											Numer konta bankowego
										</Label>
										<BankAccountInput
											disabled={isDisabled}
											value={
												transfer.msr_transfers_account_number ||
												""
											}
											onChange={(formattedValue) =>
												handleUpdateTransfer(
													"msr_transfers_account_number",
													formattedValue,
												)
											}
										/>
									</div>
									<div>
										<Label required>
											Imię i nazwisko właściciela konta
										</Label>
										<div
											style={{
												display: "flex",
												gap: "8px",
											}}
										>
											<Input
												status={
													transfer.msr_transfers_account_owner !=
													""
														? "default"
														: "error"
												}
												disabled={isDisabled}
												value={
													transfer.msr_transfers_account_owner ||
													""
												}
												onChange={(e) =>
													handleUpdateTransfer(
														"msr_transfers_account_owner",
														e.target.value,
													)
												}
											/>
											<Button
												style={{ height: "35px" }}
												onClick={() =>
													handleUpdateTransfer(
														"msr_transfers_account_owner",
														`${selectedPerson.msr_persons_name} ${selectedPerson.msr_persons_lastname}`,
													)
												}
												disabled={isDisabled}
											>
												Kopiuj dane osoby
											</Button>
										</div>
									</div>
									<div>
										<div style={labelStyle}>
											Sufiks do tytułu przelewu
										</div>
										<Input
											disabled={isDisabled}
											value={
												transfer.msr_transfers_title_suffix ||
												""
											}
											onChange={(e) =>
												handleUpdateTransfer(
													"msr_transfers_title_suffix",
													e.target.value,
												)
											}
										/>
									</div>
								</div>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr",
										alignItems: "end",
										gap: "32px",
										marginTop: "16px",
									}}
								>
									<div>
										<div style={labelStyle}>
											Tytuł przelewu
										</div>
										<div style={valueStyle}>
											{transfertitle}
										</div>
									</div>
								</div>
							</>
						)}
					</>
				)}
			</div>
			{transfer.msr_transfers_confirmed &&
			!transfer.msr_transfers_generated_file ? (
				<>
					<div
						style={{
							background: "#c99b5b",
							color: "white",
							padding: "10px 20px",
							textAlign: "center",
						}}
					>
						Przelew zatwierdzony - nie można edytować
					</div>
				</>
			) : (
				<></>
			)}
			{transfer.msr_transfers_generated_file ? (
				<>
					<div
						style={{
							background: "rgb(82, 196, 26)",
							color: "white",
							padding: "10px 20px",
							textAlign: "center",
						}}
					>
						Przelew wygenerowany - nie można edytować
					</div>
				</>
			) : (
				<></>
			)}
		</div>
	);
};

export default Transfer;
